// packages/console/src/components/panels/LiveRenditionPanel.tsx
//
// Rendition-parameters panel for the LIVE console, ported from the "Rendition"
// block of docs/design/ui_kits/aether-live/Transcoder.jsx. It edits the SELECTED
// LIVE preset (mode==="live"): rate control (CBR/VBR), CQ quality target, target
// bitrate, GOP length, B-frames, and a quality preset p1..p7. Every edit PATCHes the live
// preset through the FT-3 orchestrator (onPatch); the derived spec rows below
// (keyframe interval = gop, latency mode) recompute from the controls.
//
// The prototype's illustrative fields map onto the real Preset model: CBR/VBR
// map to rateControl cbr/vbr and the CQ slider drives liveCq (the vbr quality
// target); the target slider drives
// bitrateKbps (Mb/s in the UI, kbps in the model); GOP length drives gopLength
// (frames); the p1..p7 chips drive speedPreset. There is no session data here,
// so this panel is fully populated the moment a live preset is selected and
// shows an empty state only when the workspace has no live preset yet (R10).

import { Panel, Eb, TChip, DragSlider, Row } from "../Parts";
import { isAdminRole, canEditPreset } from "../../lib/roles";
import type { Preset, RateControl, SpeedPreset } from "../../api/jobs";
import type { PresetPatch } from "../../api/jobs";

// RC_CHIPS maps the design's rate-control labels to the model's RateControl enum.
// Live is cbr (hard constant bitrate) or vbr (quality-targeted, bitrate as a hard
// cap); the constant-quality target rides on vbr via the Quality (CQ) slider below.
// crf is not offered for live because it forbids a bitrate, and a live stream always
// needs the cap.
const RC_CHIPS: { label: string; rc: RateControl }[] = [
  { label: "CBR", rc: "cbr" },
  { label: "VBR", rc: "vbr" },
];

const PRESET_CHIPS: SpeedPreset[] = ["p1", "p3", "p4", "p5", "p7"];

export function LiveRenditionPanel({
  preset,
  onPatch,
  role,
  userId,
}: {
  preset: Preset | null;
  onPatch: (patch: PresetPatch) => void;
  // role + userId gate preset settings by OWNERSHIP: the owner edits any profile; a
  // user edits only their OWN custom profile; EVE/standard are owner-only; everyone
  // else gets a read-only notice. Source blur stays admin-only within an editable
  // view. The server enforces all of this; this is UI hiding.
  role?: string;
  userId?: string;
}) {
  const editable = canEditPreset(preset, role, userId);
  const rightTag = preset ? (
    <span style={{ font: "500 13px var(--font-mono)", color: "#fff" }}>{preset.name}</span>
  ) : undefined;

  return (
    <Panel title="Rendition" style={{ flex: 1, minHeight: 0 }} bodyStyle={{ overflowY: "auto", flex: 1 }} right={rightTag}>
      {!preset ? (
        <div style={{ font: "var(--t-micro)", color: "var(--fg4)" }}>
          No live preset for this workspace yet. Create one to configure the ladder.
        </div>
      ) : !editable ? (
        <div style={{ font: "var(--t-micro)", color: "var(--fg4)", lineHeight: 1.5 }}>
          Preset settings are managed by the workspace owner. You can run and stream with the
          selected preset ({preset.name}); changing its settings is not available for your role.
        </div>
      ) : (
        <>
          <Eb style={{ marginBottom: 11 }}>Rate control</Eb>
          <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
            {RC_CHIPS.map((c) => (
              <TChip key={c.rc} active={preset.rateControl === c.rc} onClick={() => onPatch({ rateControl: c.rc })}>
                {c.label}
              </TChip>
            ))}
          </div>
          {/* CQ value: the constant-quality target for the quality modes (VBR/CQ).
              Lower = higher quality; the target bitrate stays a hard cap. Ignored
              for CBR, so the control is hidden there. */}
          {preset.rateControl !== "cbr" && (
            <div style={{ marginBottom: 16 }}>
              <DragSlider
                label="Quality (CQ)"
                value={preset.liveCq ?? 32}
                min={0}
                max={51}
                step={1}
                unit=""
                onChange={(v) => onPatch({ liveCq: Math.round(v) })}
              />
              <div style={{ font: "var(--t-micro)", color: "var(--fg4)", marginTop: 4 }}>
                Lower = higher quality; target bitrate is the cap.
              </div>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Target bitrate in Mb/s; the model stores kbps, so scale on write. */}
            <DragSlider
              label="Target bitrate"
              value={(preset.bitrateKbps ?? 0) / 1000}
              min={0.4}
              max={24}
              step={0.1}
              unit="Mb/s"
              onChange={(v) => onPatch({ bitrateKbps: Math.round(v * 1000) })}
            />
            <DragSlider
              label="GOP length"
              value={preset.gopLength}
              min={1}
              max={300}
              step={1}
              unit="frames"
              onChange={(v) => onPatch({ gopLength: Math.round(v) })}
            />
            <DragSlider
              label="B-frames"
              value={preset.bFrames ?? 0}
              min={0}
              max={4}
              step={1}
              unit=""
              onChange={(v) => onPatch({ bFrames: Math.round(v) })}
            />
          </div>
          <div style={{ height: 1, background: "var(--line)", margin: "16px 0 12px" }} />
          <Eb style={{ marginBottom: 11 }}>Quality preset</Eb>
          <div style={{ display: "flex", gap: 5, marginBottom: 6 }}>
            {PRESET_CHIPS.map((p) => (
              <TChip key={p} active={preset.speedPreset === p} onClick={() => onPatch({ speedPreset: p })}>
                {p}
              </TChip>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              font: "var(--t-micro)",
              color: "var(--fg4)",
              marginBottom: 16,
            }}
          >
            <span>Fastest</span>
            <span>Highest quality</span>
          </div>
          <div style={{ height: 1, background: "var(--line)", margin: "16px 0 12px" }} />
          {/* Source pre-clean: a software deband pass runs before scale, so a
              low-bitrate live encode is not asked to spend bits on banding. It runs
              realtime on CPU while the encode stays on NVENC. Toggling PATCHes the
              live preset. */}
          <Eb style={{ marginBottom: 11 }}>Source pre-clean</Eb>
          <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
            <TChip active={!!preset.liveDeband} onClick={() => onPatch({ liveDeband: !preset.liveDeband })}>
              Deband
            </TChip>
          </div>
          <div style={{ font: "var(--t-micro)", color: "var(--fg4)", marginBottom: 16 }}>
            Clean the source before encode. Adds a little latency.
          </div>
          {/* Source blur: ADMIN-ONLY. A gaussian applied at full source resolution
              before the downscale, to smooth fine noisy texture (e.g. grass) so a
              low-bitrate encode does not shimmer on it. 0 = off. Hidden entirely
              for non-admins (the server also gates read + write). */}
          {isAdminRole(role) && (
            <div style={{ marginBottom: 16 }}>
              <DragSlider
                label="Source blur (admin)"
                value={preset.liveSourceBlur ?? 0}
                min={0}
                max={3}
                step={0.1}
                unit="σ"
                onChange={(v) => onPatch({ liveSourceBlur: Math.round(v * 10) / 10 })}
              />
              <div style={{ font: "var(--t-micro)", color: "var(--fg4)", marginTop: 4 }}>
                0 = off. Smooths noisy texture (grass) to stop low-bitrate shimmer; absorbed by the downscale.
              </div>
            </div>
          )}
          <div style={{ height: 1, background: "var(--line)", margin: "0 0 8px" }} />
          <Row label="Codec" value={preset.videoCodec.toUpperCase()} />
          <Row label="Keyframe" value={`${preset.gopLength} frames`} />
          <Row label="Rate control" value={preset.rateControl.toUpperCase()} />
          <Row label="Chroma" value={`4:2:0 · ${preset.bitDepth ?? 8}-bit`} />
        </>
      )}
    </Panel>
  );
}
